import { 
    collection, 
    query, 
    where, 
    getDocs, 
    addDoc, 
    updateDoc,
    getDoc,
    setDoc,
    doc
} from 'firebase/firestore';
import { auth } from './firebase.js';
import { generateSEOContent } from './ai.js';
import { createUser } from './firebase.js';
import { ref, uploadBytes, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase.js';
import axios from 'axios';

// Add URL validation helper
const validateUrl = (url) => {
    // Add https:// if not present
    if (!/^https?:\/\//i.test(url)) {
        url = 'https://' + url;
    }

    try {
        const urlObject = new URL(url);
        // Ensure it's a valid domain
        if (!urlObject.hostname.includes('.')) {
            throw new Error('Invalid domain');
        }
        return urlObject.href;
    } catch (error) {
        console.error('URL validation error:', error);
        return null;
    }
};

// Replace Firebase Functions initialization
const API_URL = 'https://craftsman-website-builder.vercel.app/api';

// Replace Firebase Functions calls with direct API calls
const callVercelApi = async (endpoint, data) => {
    console.log("end", endpoint);
    console.log("data", data);

  try {
    const response = await axios.post(`${API_URL}/${endpoint}`, data);

    console.log("res", response);

    return response.data;
  } catch (error) {
    console.error(`Error calling ${endpoint}:`, error);
    throw error;
  }
};

export const fetchCompanyData = async (cvr) => {
    try {
        console.log('Searching for CVR:', cvr);
        const data = await callVercelApi('cvrLookup', { cvr });

        if (!data || !data.name) {
            throw new Error('Kunne ikke finde firma med dette CVR nummer');
        }

        return {
            cvr,
            name: data.name || '',
            address: data.address || '',
            city: data.city || '',
            zipcode: data.zipcode || ''
        };
    } catch (error) {
        console.error('Company search error:', error);
        throw new Error('Kunne ikke hente firmaoplysninger');
    }
};

export const checkExistingCVR = async (cvr) => {
    const q = query(
        collection(db, 'companies'), 
        where('cvr', '==', cvr)
    );
    
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
};

export const createCompany = async (companyData) => {
    try {
        console.log('createCompany called with data:', { ...companyData, password: '***' });
        const { companyEmail, password, hours, showHours, ...dataWithoutPassword } = companyData;
        
        // Validate required fields
        if (!companyEmail || !password) {
            throw new Error('Email og password er påkrævet');
        }
        
        if (!companyData.cvr) {
            throw new Error('CVR-nummer er påkrævet');
        }
        
        // Create user first
        console.log('Creating user...');
        await createUser(companyEmail, password);
        console.log('User created successfully');

        // Get website data if URL exists
        let scrapedData = {
            mainContent: '',
            logo: '',
            phone: '',
            email: ''
        };

        // Check både automatisk fundet og manuelt indtastet URL
        const websiteUrl = companyData.existingWebsite?.url || 
                          (companyData.customDomain ? validateUrl(companyData.customDomain) : null);

        if (websiteUrl) {
            try {
                scrapedData = await callVercelApi('scrapeWebsite', { 
                    url: websiteUrl 
                });
            } catch (error) {
                console.error('Error scraping website:', error);
            }
        }

        // Generate subdomain from company name
        const subdomain = companyData.name
            .toLowerCase()
            .replace(/[æø]/g, (match) => match === 'æ' ? 'ae' : 'oe')
            .replace(/[å]/g, 'aa')
            .replace(/[^a-z0-9]/g, '')
            .substring(0, 30);

        // Add Vercel domain
        //const domainResult = await callVercelApi('addVercelDomain', { subdomain });
        
        // if (!domainResult.success) {
        //     throw new Error('Failed to add domain');
        // }

        // Get virk data
        console.log('Fetching virk data...');
        const virkResult = await callVercelApi('cvrLookup', { cvr: companyData.cvr });
        const virkData = virkResult || {
            phone: '',
            email: '',
            industrydesc: '',
            companydesc: '',
            startdate: '',
            employees: 0
        };
        console.log('Virk data received:', virkData);
        
        // Generate website content
        console.log('Generating SEO content...');
        const generatedContent = await generateSEOContent({
            companyName: dataWithoutPassword.name,
            mainContent: scrapedData.mainContent || '',
            industryDesc: virkData.industrydesc || '',
            companyDesc: virkData.companydesc || '',
            city: dataWithoutPassword.city, // Tilføj by
            address: dataWithoutPassword.address, // Tilføj adresse
            zipcode: dataWithoutPassword.zipcode, // Tilføj postnummer
            startDate: virkData.startdate, // Tilføj startdato
            employees: virkData.employees // Tilføj antal ansatte
        });
        console.log('SEO content generated:', generatedContent);

        // Save company document
        console.log('Saving company document to Firestore...');
        const companyRef = doc(db, 'companies', companyData.cvr);
        await setDoc(companyRef, {
            ...dataWithoutPassword,
            hours: hours || {
                Mandag: '07:00 - 16:00',
                Tirsdag: '07:00 - 16:00',
                Onsdag: '07:00 - 16:00',
                Torsdag: '07:00 - 16:00',
                Fredag: '07:00 - 15:00',
                Lørdag: 'Lukket',
                Søndag: 'Lukket',
                showHours: showHours ?? true,
            },
            domain: {
                subdomain: subdomain,
                custom: '',
                url: `${subdomain}.xn--hndvrkernet-x8aq.dk`
            },
            logo: scrapedData.logo || '',
            phone: scrapedData.phone || virkData.phone || '',
            email: scrapedData.email || virkData.email || '',
            companyEmail: companyEmail,
            industryDesc: virkData.industrydesc || '',
            companyDesc: virkData.companydesc || '',
            startDate: virkData.startdate || '',
            employees: virkData.employees || 0,
            websiteContent: {
                title: generatedContent.title || '',
                subtitle: generatedContent.subtitle || '',
                content: generatedContent.content || '',
                processedContent: {
                    title: generatedContent.title || '',
                    subtitle: generatedContent.subtitle || '',
                    content: generatedContent.content || ''
                }
            },
            createdAt: new Date(),
            status: 'active',
            plan: {
                type: 'Free',
                trialStartDate: new Date(),
                trialEndDate: new Date(Date.now() + (90 * 24 * 60 * 60 * 1000)), // 90 days
                status: 'active'
            },
            theme: 'amber'
        });
        console.log('Company document saved successfully');

        // Create default homepage in pages subcollection
        console.log('Creating default pages...');
        const pagesRef = collection(db, `companies/${companyData.cvr}/pages`);

        // First create homepage
        await addDoc(pagesRef, {
            title: 'Forside',
            slug: 'forside',
            type: 'homepage',
            layout: 'hero',
            content: {
                hero: {
                    title: generatedContent.title,
                    subtitle: generatedContent.subtitle,
                    backgroundImage: 'https://fastly.picsum.photos/id/33/5000/3333.jpg?hmac=h5NVRcUXmsWm612YQOroHSA5n9R7gxZgoP60LHBPHtw'
                },
                main: {
                    content: generatedContent.content,
                    image: 'https://fastly.picsum.photos/id/48/5000/3333.jpg?hmac=y3_1VDNbhii0vM_FN6wxMlvK27vFefflbUSH06z98so'
                }
            },
            createdAt: new Date(),
            updatedAt: new Date(),
            pageName: 'Forside',
            metaTitle: 'Forside',
            metaDescription: 'Dette er forsiden'
        });

        // Then create About page
        await addDoc(pagesRef, {
            title: 'Om os',
            slug: 'om-os',
            type: 'page',
            layout: 'text-image',
            content: {
                title: 'Om ' + companyData.name,
                subtitle: 'Lær os bedre at kende',
                main: {
                    content: `${companyData.name} er en professionel håndværkervirksomhed med mange års erfaring.\n\nVi lægger stor vægt på kvalitet og god service i alt vores arbejde.`,
                    image: 'https://fastly.picsum.photos/id/48/5000/3333.jpg?hmac=y3_1VDNbhii0vM_FN6wxMlvK27vFefflbUSH06z98so'
                }
            },
            createdAt: new Date(),
            updatedAt: new Date(),
            pageName: 'Om os',
            metaTitle: 'Om os',
            metaDescription: `Læs mere om ${companyData.name} og vores værdier`
        });
        console.log('Default pages created');

        // Send welcome email
        console.log('Sending welcome email...');
        await fetch('/api/sendWelcomeEmail', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                companyName: companyData.name,
                companyEmail: companyData.companyEmail,
                cvr: companyData.cvr,
                subdomain: subdomain
            })
        });
        console.log('Welcome email sent');

        // Return the subdomain URL for redirection
        console.log('Company creation completed successfully');
        return {
            companyRef,
            url: `https://${subdomain}.xn--hndvrkernet-x8aq.dk`
        };
    } catch (error) {
        console.error('Company creation error:', error);
        console.error('Error stack:', error.stack);
        throw error;
    }
};

export const searchExistingWebsite = async (companyName) => {
    try {
        const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
        const SEARCH_ENGINE_ID = process.env.REACT_APP_SEARCH_ENGINE_ID;

        if (!GOOGLE_API_KEY || !SEARCH_ENGINE_ID) {
            console.error('Missing Google API credentials');
            return { found: false, error: 'Search configuration missing' };
        }

        console.log('Searching for:', companyName);
        
        // Add site:.dk to search only Danish domains
        const response = await fetch(
            `https://customsearch.googleapis.com/customsearch/v1?key=${GOOGLE_API_KEY}&cx=${SEARCH_ENGINE_ID}&q=${encodeURIComponent(companyName + ' site:.dk')}&exactTerms=${encodeURIComponent(companyName)}`
        );

        if (!response.ok) {
            throw new Error(`Search failed: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.items && data.items.length > 0) {
            // First try to find a .dk domain with root path
            let mainSite = data.items.find(item => {
                try {
                    const url = new URL(item.link);
                    return url.hostname.endsWith('.dk') && 
                           (url.pathname === '/' || url.pathname === '');
                } catch (e) {
                    return false;
                }
            });

            // If no root .dk domain found, take first .dk domain
            if (!mainSite) {
                mainSite = data.items.find(item => {
                    try {
                        const url = new URL(item.link);
                        return url.hostname.endsWith('.dk');
                    } catch (e) {
                        return false;
                    }
                });
            }

            // Fallback to first result if no .dk domain found
            mainSite = mainSite || data.items[0];

            console.log('Found website:', mainSite);
            return {
                found: true,
                url: mainSite.link,
                title: mainSite.title,
                description: mainSite.snippet
            };
        }
        
        return { found: false };
    } catch (error) {
        console.error('Website search error:', error);
        return { found: false, error: error.message };
    }
};

export const scrapeWebsiteContent = async (url) => {
    try {
        const result = await callVercelApi('scrapeWebsite', { url });
        return result;
    } catch (error) {
        console.error('Website scraping error:', error);
        return null;
    }
};

export const getAllCompanies = async () => {
    try {
        const querySnapshot = await getDocs(collection(db, 'companies'));
        return querySnapshot.docs.map(doc => ({
            ...doc.data(),
            id: doc.id
        }));
    } catch (error) {
        console.error('Error fetching companies:', error);
        throw error;
    }
};

// Update getCompany to search by CVR
export const getCompany = async (cvr) => {
    try {
        const q = query(collection(db, 'companies'), where('cvr', '==', cvr));
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
            throw new Error('Firma ikke fundet');
        }
        
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
    } catch (error) {
        console.error('Error fetching company:', error);
        throw error;
    }
};

export const updateCompany = async (cvr, updateData) => {
    try {
        const q = query(collection(db, 'companies'), where('cvr', '==', cvr));
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
            throw new Error('Firma ikke fundet');
        }
        
        const doc = querySnapshot.docs[0];
        
        // Update the document
        await updateDoc(doc.ref, {
            ...updateData,
            updatedAt: new Date()
        });

        // Return updated data
        const updatedDoc = await getDoc(doc.ref);
        return { id: updatedDoc.id, ...updatedDoc.data() };
    } catch (error) {
        console.error('Error updating company:', error);
        throw new Error('Kunne ikke opdatere firma information');
    }
};

// Add this new function
export const uploadLogo = async (file, cvr) => {
    try {
        if (!file) throw new Error('No file provided');

        // Create a storage reference
        const storageRef = ref(storage, `logos/${cvr}/${Date.now()}-${file.name}`);
        
        // Upload the file
        await uploadBytes(storageRef, file);
        
        // Get the download URL
        const downloadURL = await getDownloadURL(storageRef);
        
        return downloadURL;
    } catch (error) {
        console.error('Error uploading logo:', error);
        throw new Error('Kunne ikke uploade logo');
    }
};

export const uploadPageImage = async (file, cvr) => {
    try {
        if (!file) throw new Error('No file provided');
        if (!cvr) throw new Error('No CVR provided');
        
        const storageRef = ref(storage, `images/${cvr}/${Date.now()}-${file.name}`);
        
        // Add metadata to upload
        const metadata = {
            contentType: file.type,
            customMetadata: {
                uploadedBy: auth.currentUser?.email,
                cvr: cvr
            }
        };
        
        // Upload with metadata
        await uploadBytes(storageRef, file, metadata);
        console.log("Upload successful, getting URL...");
        
        const downloadURL = await getDownloadURL(storageRef);
        console.log("Got download URL:", downloadURL);
        
        return downloadURL;
    } catch (error) {
        console.error('Detailed upload error:', {
            error,
            code: error.code,
            message: error.message,
            storageError: error.serverResponse,
            auth: {
                user: !!auth.currentUser,
                email: auth.currentUser?.email
            }
        });
        throw error;
    }
};

export const getPages = async (cvr) => {
    try {
        if (!cvr) {
            throw new Error('CVR er påkrævet');
        }
        
        // Get pages from subcollection
        const pagesRef = collection(db, `companies/${cvr}/pages`);
        const snapshot = await getDocs(pagesRef);
        
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error getting pages:', error);
        throw error;
    }
};

export const getCompanies = async () => {
    try {
        const companiesRef = collection(db, 'companies');
        const snapshot = await getDocs(companiesRef);
        
        // Get companies with their pages
        const companies = await Promise.all(snapshot.docs.map(async doc => {
            const company = { id: doc.id, ...doc.data() };
            
            // Get pages for each company
            const pagesSnapshot = await getDocs(collection(db, `companies/${company.cvr}/pages`));
            company.pages = pagesSnapshot.docs.map(pageDoc => ({
                id: pageDoc.id,
                ...pageDoc.data()
            }));
            
            return company;
        }));
        
        return companies;
    } catch (error) {
        console.error('Error getting companies:', error);
        throw error;
    }
};

export const getUploadedImages = async (cvr) => {
    try {
        const listRef = ref(storage, `images/${cvr}`);
        const result = await listAll(listRef);
        
        // Get all image URLs
        const imageUrls = await Promise.all(
            result.items.map(async (itemRef) => {
                const url = await getDownloadURL(itemRef);
                return {
                    name: itemRef.name,
                    url: url,
                    path: itemRef.fullPath
                };
            })
        );
        
        return imageUrls;
    } catch (error) {
        console.error('Error getting uploaded images:', error);
        throw new Error('Kunne ikke hente billeder');
    }
};

export const deletePageImage = async (imagePath, cvr) => {
    try {
        const imageRef = ref(storage, imagePath);
        await deleteObject(imageRef);
    } catch (error) {
        console.error('Error deleting image:', error);
        throw new Error('Kunne ikke slette billedet');
    }
};

export const checkExistingEmail = async (email) => {
    if (!email) return false;

    const q = query(
        collection(db, 'companies'), 
        where('companyEmail', '==', email)
    );
    
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
};

export const getCompanyByDomain = async (domain) => {
    const companiesRef = collection(db, 'companies');
    const q = query(companiesRef, where('domain.custom', '==', domain));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
        return {
            id: querySnapshot.docs[0].id,
            ...querySnapshot.docs[0].data()
        };
    }
    return null;
};